import { MigrationService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, Box, Button, ExpandableSection, FormField, Grid, NonCancelableCustomEvent, 
    Select, SelectProps, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import { isVmwareEnabled, regions } from '../../common/regions';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { generatePluralStr } from '../../common/util';
import { TcoContext } from '../../contexts/TcoContext';
import { ColumnOrderType } from '../portfolio-data-asset/constants';
import { SchemaPropertyMap } from '../portfolio-data-asset/types';
import { generateColumnDefs } from '../portfolio-data-asset/utils';
import { getMigrationStrategyOptions, serverResourceName } from './constants';
import { ServerWithMigrationStrategy } from './interfaces';

type Props = FlashContextInterface & {
    scoredServers: ServerWithMigrationStrategy[];
    onMigrationOverride: () => Promise<void>;
    migrationStrategyAssessmentId: string;
    showRecalculateAlert: boolean;
};

const schemaService = new SchemaService();
const migrationService = new MigrationService();

const EXCLUDED_COLUMNS = ['portfolioId', 'serverId', 'migrationPlan', 'migrationPlanOverride'];

function MigrationStrategyServerTable({ flash, scoredServers, onMigrationOverride, migrationStrategyAssessmentId, showRecalculateAlert }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const portfolioId = match?.params?.portfolioId ?? '';
    const isMounted = useIsMounted();

    const [ loading, setLoading ] = useState(false);
    const [ serverItems, setServerItems ] = useState<ServerWithMigrationStrategy[]>([]);
    const [ serverColumnDefinitions, setServerColumnDefinitions ] = useState<ColumnDefinition<ServerWithMigrationStrategy>[]>([]);
    const [ selectedServers, setSelectedServers ] = useState<ServerWithMigrationStrategy[]>([]);
    const [ migrationStrategyUpdateSelectedOption, setMigrationStrategyUpdateSelectedOption ] = useState<OptionDefinition | null>(null);
    const [ regionSupportsRelocate, setRegionSupportsRelocate ] = useState(false);
    const [ showOverrideInfo, setShowOverrideInfo ] = useState(true);

    const { assessment } = useContext(TcoContext);

    useEffect(() => {
        const loadServerData = async () => {
            if (!isMounted()) {
                return;
            }
            setLoading(true);
            try {
                if (!portfolioId) {
                    flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                    return;
                }

                setServerItems(scoredServers);

                const serverSchemas = await schemaService.listAttributes(serverResourceName, portfolioId);
                const serverHeaders: SchemaPropertyMap = {};
                serverSchemas.forEach(serverSchema => {
                    serverHeaders[serverSchema.value] = serverSchema;
                });

                const columnDefinitions = generateColumnDefs(scoredServers, serverHeaders, ColumnOrderType.SERVER, EXCLUDED_COLUMNS);
                const col = columnDefinitions.find(col => col.id === 'applicationMappings');
                if (col) {
                    col.text = constants.MAPPED;
                }
                
                const columns: ColumnDefinition<ServerWithMigrationStrategy>[] = [
                    {
                        header: constants.SERVER_ID,
                        type: ColumnTypes.TEXT,
                        id: 'id',
                        cell: item => item.serverId, 
                        sortingField: 'id',
                        text: constants.SERVER_ID,
                    },
                    {
                        header: constants.MIGRATION_STRATEGY,
                        type: ColumnTypes.TEXT, 
                        id: 'migrationStrategy',
                        cell: item => item.migrationStrategy,
                        sortingField: 'migrationStrategy',
                        text: constants.MIGRATION_STRATEGY,
                    },
                    {
                        header: constants.RECOMMENDED_TOOL,
                        type: ColumnTypes.TEXT, 
                        id: 'recommendedTools',
                        cell: item => item.recommendedTools,
                        sortingField: 'recommendedTools',
                        text: constants.RECOMMENDED_TOOL,
                    },
                    {
                        header: constants.OVERRIDDEN,
                        type: ColumnTypes.BOOLEAN,
                        id: 'isOverridden',
                        data: item => item.isOverridden ? constants.TRUE : constants.FALSE,
                        cell: item => item.isOverridden ? constants.TRUE : constants.FALSE,
                        sortingField: 'isOverridden',
                        text: constants.OVERRIDDEN,
                    },
                ];
                setServerColumnDefinitions(columns.concat(columnDefinitions));
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.SERVER_ERROR_LOADING);
                    setServerItems([]);
                    setServerColumnDefinitions([]);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }   
            }
        };

        loadServerData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scoredServers]);

    useEffect(() => {
        if (!assessment) {
            return;
        }
        const currentRegion = assessment.configuration.tcoAssumptions.awsRegion;
        setRegionSupportsRelocate(isVmwareEnabled(regions[currentRegion]));
    }, [assessment]);

    const defaultSortingState: SortingState<ServerWithMigrationStrategy> = {
        isDescending: false,
        sortingColumn: {
            sortingField: 'id',
        },
    };

    const migrationStrategyOverrideChanged = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setMigrationStrategyUpdateSelectedOption(event.detail.selectedOption ?? null);
    };

    const migrationStrategyBulkUpdate = async (): Promise<void> => {
        if (selectedServers.length === 0 || !migrationStrategyUpdateSelectedOption) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_MISSING_VALUES, constants.BULK_UPDATE_FAILED);
            return;
        }

        if (!assessment || !migrationStrategyAssessmentId) {
            return;
        }
 
        setLoading(true);
        try {
            const selectedServerIds = selectedServers.map(server => server.serverId);
            const conditions = {
                portfolioId: portfolioId,
                serverIds: selectedServerIds,
                overrideValue: migrationStrategyUpdateSelectedOption.value,
                tcoId: assessment.id,
            };
            await migrationService.override(migrationStrategyAssessmentId, conditions);
            await onMigrationOverride();
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, constants.ERROR_UPDATING_MIGRATION_STRATEGY, '');
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    const updateButtonText = `${constants.UPDATE_BUTTON_TEXT} ${selectedServers.length} ${generatePluralStr(selectedServers.length, constants.ITEM_LOWERCASE)}`;

    const overrideMigrationStrategy = (
        <ExpandableSection
            header={constants.OVERRIDE_MIGRATION_STRATEGY}
            defaultExpanded
        >
            <SpaceBetween size='s'>
                { showOverrideInfo ? (
                    <Alert
                        data-testid='overrideInfoAlert'
                        dismissible 
                        type='info'
                        onDismiss={() => setShowOverrideInfo(false)}
                    >{constants.OVERRIDE_INFO}</Alert>
                ) : null }
                <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                    <FormField
                        label={constants.SELECT_OVERRIDE_STRATEGY}
                    >
                        <Select
                            placeholder={constants.MIGRATION_STRATEGIES}
                            options={getMigrationStrategyOptions(regionSupportsRelocate)}
                            selectedOption={migrationStrategyUpdateSelectedOption}
                            onChange={migrationStrategyOverrideChanged}
                        ></Select>
                    </FormField>
                    <Box float='right' display='inline'>
                        <Button
                            variant='primary'
                            disabled={selectedServers.length === 0 || !migrationStrategyAssessmentId || !migrationStrategyUpdateSelectedOption}
                            onClick={migrationStrategyBulkUpdate}
                        >
                            {
                                selectedServers.length === 0 ? constants.UPDATE_BUTTON_TEXT : updateButtonText
                            }
                        </Button>
                    </Box>
                </Grid>
                { showRecalculateAlert ? <Alert type='warning'>{constants.RECALCULATE_ALERT}</Alert> : null }
            </SpaceBetween>
        </ExpandableSection>
    );

    return loading ? <Spinner size='big'/> : (
        <SpaceBetween size='m'>
            {overrideMigrationStrategy}
            <AWSCATTable
                resourceName={constants.SERVER_RESOURCE_NAME}
                items={serverItems}
                columnDefinitions={serverColumnDefinitions}
                selectionType='multi'
                onSelectionChange={setSelectedServers as () => void}
                enableAutoSorting
                defaultSortBy={defaultSortingState}
                autoGenerateVisibleContent
                useDefaultDownload
            />
        </SpaceBetween>
    );

}

export default withFlashContext(MigrationStrategyServerTable);
